import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Stack,
  Typography,
  Button,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Menu,
  MenuItem,
  useTheme,
} from "@mui/material";
import { Search, MoreVert } from "@mui/icons-material";

import { Strings } from "@app/constants";
import { FormProvider, RHFTextField } from "@app/components";
import { useDialog, useRequestState } from "@app/hooks";
import { AddCategoryDialog } from "./new category-dialog";
import { ParentCategoryDialog } from "./parent-catrgory-dialog";
import { category, deleteCategory } from "./slice";
import {
  selectAllCategoryDataState,
  selectDeleteCategoryState,
} from "./selector";

const CategoryPage = () => {
  const methods = useForm(); 
  const theme = useTheme();
  const [selectedRow, setSelectedRow] = useState<number | null>(null);
  const [editCategoryId, setEditCategoryId] = useState<number | null>(null);
  const [menuanchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const addCategoryDialog = useDialog();
  const addParentCategoryDialog = useDialog();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, id: number) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedRow(id);
  };

  const handleEditCategory = () => {
    setEditCategoryId(selectedRow);
    addCategoryDialog.show();
    handleMenuClose();
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedRow(null);
  };

  const categoryData = useSelector(selectAllCategoryDataState);
  const categories = categoryData?.data?.data || [];

  const itemsPerPage = 8;
  const filteredData = categories.filter((item: any) =>
    [item.name, item.parentCategory?.name || ""].some((field) =>
      field.toLowerCase().includes(search.toLowerCase())
    )
  );

  const displayedProducts = filteredData.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    addParentCategoryDialog.show();
  };

  const handleClose = () => {
    addParentCategoryDialog.hide();
  };

  const handleCategoryOpen = (event: React.MouseEvent<HTMLElement>) => {
    addCategoryDialog.show();
    setEditCategoryId(null);
  };

  const handleCategoryClose = () => {
    addCategoryDialog.hide();
    setEditCategoryId(null);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const dispatch = useDispatch();

  useRequestState({
    stateSelector: selectDeleteCategoryState,
    successMessageShown: true,
    errorShown: true,
  });

  useEffect(() => {
    dispatch(category());
  }, [dispatch]);

  const fontFamily = Strings.fontLanguage.workSans;

  return (
    <FormProvider methods={methods}>
      <Stack direction="row" sx={{ minHeight: "100vh", bgcolor: "#F5F5F5" }}>
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <Box sx={{ flex: 1, overflowY: "auto" }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", sm: "center" }}
              flexWrap="wrap"
              rowGap={2}
              mb={3}
            >
              <RHFTextField
                name="search"
                placeholder="Search"
                size="small"
                value={search}
                onChange={handleSearchChange}
                sx={{ flex: 1, maxWidth: 600 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
              <Stack direction="row" spacing={2} alignItems="center">
                <Button
                  variant="contained"
                  sx={{
                    textTransform: "none",
                    backgroundColor: theme.palette.primary.main,
                    color: "white",
                    fontFamily: fontFamily,
                    fontWeight: "700",
                  }}
                  onClick={handleMenuClick}
                >
                  {Strings.common.parentCategory}
                </Button>
                <Button
                  variant="contained"
                  sx={{
                    textTransform: "none",
                    backgroundColor: theme.palette.primary.main,
                    color: "white",
                    fontFamily: fontFamily,
                    fontWeight: "700",
                  }}
                  onClick={handleCategoryOpen}
                >
                  {Strings.common.addNewCategory}
                </Button>
              </Stack>
            </Stack>

            <TableContainer component={Paper} sx={{ pb: 3 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    {["Name", "Parent Category", "Action"].map((heading) => (
                      <TableCell
                        key={heading}
                        sx={{
                          fontWeight: 600,
                          fontFamily: fontFamily,
                          color: "#474747",
                        }}
                        align="center"
                      >
                        {heading}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedProducts.length > 0 ? (
                    displayedProducts.map((item: any, index: any) => (
                      <TableRow key={index}>
                        <TableCell
                          sx={{ color: "#5F5F5F", fontWeight: 600 }}
                          align="center"
                        >
                          {item.name}
                        </TableCell>
                        <TableCell
                          sx={{ color: "#5F5F5F", fontWeight: 600 }}
                          align="center"
                        >
                          {item.parentCategory?.name || "N/A"}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            onClick={(e) => {
                              handleMenuOpen(e, item.id);
                            }}
                          >
                            <MoreVert />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        {search
                          ? "No category found matching your search"
                          : "No category found"}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                spacing={2}
                mt={2}
              >
                <Typography
                  sx={{ fontSize: 14, fontFamily: fontFamily, fontWeight: 400 }}
                >
                  {`Showing ${
                    filteredData?.length === 0
                      ? 0
                      : (page - 1) * itemsPerPage + 1
                  } to ${Math.min(
                    page * itemsPerPage,
                    filteredData?.length
                  )} of ${filteredData.length} entries`}
                </Typography>
                <Pagination
                  count={Math.ceil(filteredData.length / itemsPerPage)}
                  page={page}
                  onChange={(event, value) => setPage(value)}
                  shape="rounded"
                />
              </Stack>
            </TableContainer>
          </Box>
        </Box>
        <Menu
          anchorEl={menuanchorEl}
          open={Boolean(menuanchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleEditCategory}>Edit</MenuItem>
          <MenuItem
            onClick={() => {
              handleMenuClose();
              if (selectedRow !== null) {
                dispatch(deleteCategory({ id: selectedRow }));
              }
            }}
            sx={{ color: "error.main" }}
          >
            Delete
          </MenuItem>
        </Menu>

        {addCategoryDialog.visible && (
          <AddCategoryDialog
            open
            onClose={handleCategoryClose}
            categoryId={editCategoryId}
            categories={categories}
          />
        )}

        {addParentCategoryDialog.visible && (
          <ParentCategoryDialog open onClose={handleClose} />
        )}
      </Stack>
    </FormProvider>
  );
};

export { CategoryPage };
